<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Team Roles" @click="$router.push('/settings/teamroles')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                </v-toolbar>
            </v-card>
            <v-card class="elavation-1 transparent" style="margin-top:5px;">
                <v-toolbar flat height="45px">
                    <b>Details</b>
                    <v-spacer></v-spacer>
                    <v-btn v-if="!loading&&!isReadOnly" @click="editTeamRole" icon title="Edit team role name or description">
                        <v-icon small>edit</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <v-layout row wrap>
                        <v-flex xs12 sm6>
                            <v-layout :class="isMobile? 'form-row-mobile' : 'form-row'"> 
                                <v-flex xs5 class="text-xs-left">
                                    Name :
                                </v-flex>
                                <v-flex xs7 class="font-weight-bold text-xs-left">
                                    {{ role.name }}
                                </v-flex>  
                            </v-layout>
                            <v-layout :class="isMobile? 'form-row-mobile' : 'form-row'"> 
                                <v-flex xs5 class="text-xs-left">
                                    Type :
                                </v-flex>
                                <v-flex xs7 class="font-weight-bold text-xs-left">
                                    <span v-if="role.isSystemRole">System Predefined</span>
                                    <span v-else>User Defined</span>
                                </v-flex>  
                            </v-layout>
                            <v-layout :class="isMobile? 'form-row-mobile' : 'form-row'"> 
                                <v-flex xs5 class="text-xs-left">
                                    Description :
                                </v-flex>
                                <v-flex xs7 class="font-weight-bold text-xs-left">
                                    {{ role.description }}
                                </v-flex>  
                            </v-layout>
                        </v-flex>
                        <v-flex xs12 sm6>
                            <v-layout :class="isMobile? 'form-row-mobile' : 'form-row'"> 
                                <v-flex xs5 class="text-xs-left">
                                    Created By :
                                </v-flex>
                                <v-flex xs7 class="font-weight-bold text-xs-left">
                                    <span v-if="role.isSystemRole">System</span>
                                    <v-chip v-else small style="margin:-5px 0px 0px -5px;">
                                        <v-avatar>
                                            <v-img :src="awsS3Helper.getAvatarUrl(role.createdByUser.userId)"></v-img>
                                        </v-avatar>
                                        <b>{{role.createdByUser.firstName}} {{role.createdByUser.lastName}}</b>
                                    </v-chip>
                                </v-flex>  
                            </v-layout>
                            <v-layout :class="isMobile? 'form-row-mobile' : 'form-row'"> 
                                <v-flex xs5 class="text-xs-left">
                                    Created On :
                                </v-flex>
                                <v-flex xs7 class="font-weight-bold text-xs-left">
                                    {{ role.createdOn | moment('MMM DD, YYYY') }}
                                </v-flex>  
                            </v-layout>
                            <v-layout :class="isMobile? 'form-row-mobile' : 'form-row'"> 
                                <v-flex xs5 class="text-xs-left">
                                    Show in My Teams :
                                </v-flex>
                                <v-flex xs7 class="font-weight-bold text-xs-left">
                                    <span v-if="role.visibleInMyTeams">Yes</span>
                                    <span v-else>No</span>
                                </v-flex>  
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>
            <v-alert v-if="role.isSystemRole" :value="true" color="info" icon="info" outline dismissible>
                System-generated team roles are read-only. To support a different use case, create a new team role and configure its permissions.
            </v-alert> 
            <v-card class="elavation-1 transparent" style="margin-top:5px;">
                <v-toolbar flat height="45px">
                    <b>Permissions</b>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="headers"
                    :items="permissions"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                >
                    <template v-slot:items="props">
                        <tr>
                            <td>{{ props.item.pageName }}</td>
                            <td>
                                <v-switch v-model="props.item.visible" :readonly="isReadOnly" color="primary" @change="updatePermission(props.item, 'visible')" :title="getTooltip('Visible', props.item.visible, props.item.pageName)" class="switch-item"></v-switch>
                            </td>
                            <td>
                                <v-switch v-model="props.item.view" :readonly="isReadOnly" color="primary" @change="updatePermission(props.item, 'view')" :title="getTooltip('View', props.item.view, props.item.pageName)" class="switch-item"></v-switch>
                            </td>
                            <td>
                                <v-switch v-model="props.item.edit" :readonly="isReadOnly" color="primary" @change="updatePermission(props.item, 'edit')" :title="getTooltip('Edit', props.item.edit, props.item.pageName)" class="switch-item"></v-switch>
                            </td>
                            <td>
                                <v-switch v-model="props.item.approver" :readonly="isReadOnly" color="primary" @change="updatePermission(props.item, 'approver')" :title="getTooltip('Approver', props.item.approver, props.item.pageName)" class="switch-item"></v-switch>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>

            <!-- Comments and Updates -->
            <v-layout row wrap>
                <v-flex xs12 sm6>
                    <!-- comments history component -->
                    <comments-history v-if="!loading" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading" ref="updateHistoryRef" :page_query_id="pageQueryId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>

        <v-dialog v-model="dialogAddEdit" persistent width="600">
            <add-edit ref="addEditRole" :dialogAddEdit.sync="dialogAddEdit" @send="getPermissions(false)"></add-edit>
        </v-dialog>

        <!-- status messaging component -->
        <status-messaging :page_query_id="pageQueryId" @refresh="updateHandler"></status-messaging>
    </v-layout> 
</template>

<script>
import commonfn from '@/common/functions'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'
import teamRolesApi from '@/api/teamroles'
import notificationTypes from '@/common/notificationTypes'
import pageKeys from '@/common/pagekeys'
import awsS3Helper from '@/helpers/aws-s3'

export default {     
    name: 'TeamRoleDetails',
    metaInfo: {
        title: 'Team Role Details',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Team Role Details' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory'),
        'add-edit': () => import('@/components/TeamRoles/AddEditTeamRole'),
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)

        return {
            error: false,
            errorMessage: '',
            loading: false,
            dialogAddEdit: false,
            rowsPerPageItems,
            awsS3Helper,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            headers: [
                {text: 'Page',align: 'left',value: 'pageName',width: '20%', sortable:true},
                {text: 'Visible',align: 'left',value: 'visible',width: '10%', sortable:false},
                {text: 'View',align: 'left',value: 'view',width: '10%', sortable:false},
                {text: 'Edit',align: 'left',value: 'edit',width: '10%', sortable:false},
                {text: 'Approver',align: 'left',value: 'approver',width: '10%', sortable:false}
            ],
            role: null,
            permissions: []
        }
    },
    methods: {
        async updatePermission(item, fieldChanged) {
            try {
                const cloned = JSON.parse(JSON.stringify(item));

                // --- CASE 1: user turned OFF VIEW ---
                if (fieldChanged === 'view' && !cloned.view) {
                    cloned.edit = false;
                    cloned.approver = false;
                }

                // --- CASE 2: user turned ON EDIT or APPROVER ---
                if ((fieldChanged === 'edit' && cloned.edit) ||
                    (fieldChanged === 'approver' && cloned.approver)) {
                    cloned.view = true;
                }

                // --- Call API ---
                await teamRolesApi.updatePermissions(cloned);

                // --- Update UI ---
                Object.assign(item, cloned);

            } catch (error) {
                this.error = true;
                this.errorMessage = error;
            }
        },
        editTeamRole() {
            this.$refs.addEditRole.setEdit(this.role)
            this.dialogAddEdit = true
        },
        getTooltip(title, value, pageName) {
            if (!this.role.isSystemRole) {
                const actions = {
                    Visible: {
                        allowed: `Click to make the user visible on the ${pageName} page`,
                        notAllowed: `Click to make the user not visible on the ${pageName} page`
                    },
                    View: {
                        allowed: `Click to allow the user to view the ${pageName} page`,
                        notAllowed: `Click to not allow the user to view the ${pageName} page`
                    },
                    Edit: {
                        allowed: `Click to allow the user to edit data on the ${pageName} page`,
                        notAllowed: `Click to not allow the user to edit data on the ${pageName} page`
                    },
                    Approver: {
                        allowed: `Click to allow the user to approve data on the ${pageName} page`,
                        notAllowed: `Click to not allow the user to approve data on the ${pageName} page`
                    }
                };

                const actionText = value ? actions[title].notAllowed : actions[title].allowed;
                return actionText;
            } else {
                return 'System-generated role. Editing is not allowed.';
            }
        },
        async getPermissions(show_loading) {
           try {
                this.loading = show_loading
                const response = await teamRolesApi.getPermissions(this.selectedTeamRoleId) 
                if (response && response.status == 200) {
                    this.role = response.data.role
                    this.permissions = response.data.role.permissions
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        updateHandler(data) {
            if (data!=this.$store.state.userId) {
                this.getPermissions(false)
            }
            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        },
    },
    async created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: 'Team Membership Role Details',
            headerPageTitle: ''
        })

        this.getPermissions(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        hasEditRights() {
            return commonfn.hasEditRights(this.$router.currentRoute.path)
        },
        selectedTeamRoleId() {
            return this.$route.params.teamRoleId
        },
        pageQueryId() {
            return this.selectedTeamRoleId
        },
        commentsData() {
            return {
                pageQueryId: this.pageQueryId,
                pageItemCreatedBy: null,
                pageLink: this.$router.currentRoute.path,
                pageKey: pageKeys.team_roles,
                notificationTypeId: notificationTypes.team_roles
            }
        },
        isMobile() {
            return window.innerWidth < 600
        },
        isReadOnly() {
            return !this.hasEditRights || this.role.isSystemRole
        }
    }
}
</script>
<style scoped>
.form-row {
    padding:0px 20px 0px 50px;
}
.form-row-mobile {
    padding:0px 20px 0px 20px;
}
.switch-item {
    margin-bottom:-15px;
}
</style>